import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  isBackendDown,
  subscribeBackendHealth,
  probeBackend
} from '../utils/backendHealth';
import { isLocalDevHost } from '../utils/apiClient';

/**
 * Full-width alert shown while the backend is unreachable (see
 * utils/backendHealth.js for how that is decided). One high-contrast look on
 * purpose — an outage banner reads the same in light and dark mode.
 *
 * Localhost wording tells you the dev server died and how to bring it back;
 * production wording points at the connection instead.
 */
export default function BackendDownBanner() {
  const [down, setDown] = useState(isBackendDown);
  const [checking, setChecking] = useState(false);

  useEffect(() => subscribeBackendHealth(setDown), []);

  if (!down) return null;

  const local = isLocalDevHost();

  const retryNow = async () => {
    setChecking(true);
    try {
      await probeBackend();
    } finally {
      setChecking(false);
    }
  };

  return createPortal(
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10090,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap',
        padding: '10px 16px',
        background: '#7f1d1d',
        color: '#fef2f2',
        borderBottom: '2px solid #fca5a5',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
        fontSize: 13.5,
        lineHeight: 1.4
      }}
    >
      <span style={{ fontSize: 17 }} aria-hidden="true">🔌</span>
      <span style={{ fontWeight: 700 }}>
        {local ? 'Local backend is not running' : 'Cannot reach the server'}
      </span>
      <span style={{ opacity: 0.9 }}>
        {local
          ? 'Nothing will load or save. Start it with npm run start (or start:backend). This clears itself when the server is back.'
          : 'Check your internet connection. This clears itself when the connection returns.'}
      </span>
      <button
        type="button"
        onClick={retryNow}
        disabled={checking}
        style={{
          padding: '4px 12px',
          borderRadius: 7,
          border: '1px solid #fca5a5',
          background: 'transparent',
          color: '#fef2f2',
          fontWeight: 700,
          fontSize: 12.5,
          cursor: checking ? 'default' : 'pointer',
          opacity: checking ? 0.6 : 1
        }}
      >
        {checking ? 'Checking…' : 'Retry now'}
      </button>
    </div>,
    document.body
  );
}
